import {
  AmbientLight,
  Box3,
  Camera,
  Color,
  FrontSide,
  GridHelper,
  HemisphereLight,
  MOUSE,
  MathUtils,
  Mesh,
  MeshPhysicalMaterial,
  Object3D,
  Object3DEventMap,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  Vector3,
  WebGLRenderer
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
// @ts-ignore
import { TrackballControls } from '../trackballcontrols';

import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Selector } from './selector';
import { SessionService } from '../../services/session.service';
import { Engine } from '../engine';
import { DimensionsHelper } from '../engine.types';
import { ViewCubeHelper } from './view-cube-helper';

export class Controls {
  static createEngine(scene: any, canvas: any) {
    const renderer = new WebGLRenderer({
      powerPreference: 'high-performance',
      canvas: canvas,
      antialias: true,
      stencil: true,
      depth: true
    });
    renderer.autoClear = false;
    renderer.localClippingEnabled = false;
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0xffffff);

    return renderer;
  }

  static createCSS2DRenderer(canvas: any, width: number, height: number) {
    const renderer = new CSS2DRenderer({ element: canvas });
    renderer.setSize(width, height);
    renderer.domElement.style.position = 'absolute';
    renderer.domElement.style.top = '0px';
    const canvasContainer = document.getElementById('canvas-container');
    if (canvasContainer) {
      canvasContainer.appendChild(renderer.domElement);
    }
    return renderer;
  }

  static createScene() {
    const scene = new Scene();
    scene.add(new HemisphereLight(0xffffff, 0xaaaaaa, 10));
    scene.add(new AmbientLight(0xffffff, 3));

    return scene;
  }

  static createCamera(scene: any) {
    const camera = new OrthographicCamera(10, 10, 10, 10, 0.1, 1000);
    camera.layers.set(0);
    scene.add(camera);

    return camera;
  }

  static createOrbitControls(camera: Camera, canvas: any) {
    const controls = new OrbitControls(camera, canvas);
    controls.mouseButtons = {
      LEFT: null,
      MIDDLE: MOUSE.PAN,
      RIGHT: MOUSE.ROTATE
    };
    controls.enableDamping = false;
    controls.rotateSpeed = 0.5;
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.zoomToCursor = true;

    return controls;
  }

  static createTrackballControls(camera: OrthographicCamera, canvas: any, scene: any) {
    const controls = new TrackballControls(camera, canvas);
    controls.rotateSpeed = 2;
    controls.zoomSpeed = 2;
    controls.panSpeed = 1;
    controls.noZoom = true;
    controls.staticMoving = true;
    // @ts-ignore
    controls.zoomToCursor = true;

    let targetZoom = camera.zoom;
    let zooming = false;
    let targetPosition = new Vector3();
    let targetControlsTarget = new Vector3();

    controls.domElement.addEventListener('wheel', (event: any) => {
      event.preventDefault();

      const { clientWidth, clientHeight } = controls.domElement;
      const rect = controls.domElement.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / clientWidth) * 2 - 1;
      const y = -((event.clientY - rect.top) / clientHeight) * 2 + 1;

      // @ts-ignore
      const zoomToCursor = controls.zoomToCursor !== false;
      let direction = new Vector3();

      if (zoomToCursor) {
        const cursorPosition = new Vector3(x, y, 0);
        cursorPosition.unproject(camera); // Get world coordinates under cursor
        direction.copy(cursorPosition).sub(camera.position);
      } else {
        direction.copy(controls.target).sub(camera.position);
      }

      // Zoom factor
      const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
      targetZoom = camera.zoom * zoomFactor;

      // Calculate target camera position and controls target
      const moveOffset = direction.multiplyScalar(1 - 1 / zoomFactor);
      targetPosition.copy(camera.position).add(moveOffset);
      if (zoomToCursor) {
        targetControlsTarget.copy(controls.target).add(moveOffset);
      } else {
        targetControlsTarget.copy(controls.target);
      }

      // Set zooming flag
      zooming = true;

      // Start animation loop
      smoothZoom();
    });

    function smoothZoom() {
      if (!zooming) return;

      // Interpolate zoom
      camera.zoom = MathUtils.lerp(camera.zoom, targetZoom, 0.5);

      // Interpolate camera position and controls target
      camera.position.lerp(targetPosition, 0.5);
      controls.target.lerp(targetControlsTarget, 0.5);

      camera.updateProjectionMatrix();
      controls.update();

      // Continue animating while not at the target values
      const zoomDifference = Math.abs(camera.zoom - targetZoom);
      const positionDifference = camera.position.distanceTo(targetPosition);

      if (zoomDifference > 0.1 || positionDifference > 0.1) {
        requestAnimationFrame(smoothZoom); // Keep animating
      } else {
        zooming = false; // Stop when close enough to the target
      }
    }

    return controls;
  }

  static createCameraCubeControls(engine: Engine, viewHelperContainer: HTMLDivElement) {
    const viewHelper = new ViewCubeHelper(
      engine,
      engine.camera,
      engine.controls,
      engine.group,
      engine.renderer.domElement,
      viewHelperContainer
    );

    viewHelperContainer.addEventListener('pointerdown', viewHelper.handlePointerDown);
    viewHelperContainer.addEventListener('pointermove', viewHelper.handlePointerMove);
    viewHelperContainer.addEventListener('pointerup', viewHelper.handlePointerUp);
    viewHelperContainer.addEventListener('pointerleave', viewHelper.handlePointerLeave);
    return viewHelper;
  }

  static createRaycaster(camera: any, canvas: any, groups: any, group: any, objects: any, engine?: Engine) {
    return new Selector(camera, canvas, groups, group, objects, engine);
  }

  static fitCameraToCenteredObject(camera: any, size: any, center: Vector3) {
    const maxSize = Math.ceil(Math.max(size.x, size.y, size.z));
    // Account for model distance from origin so far objects aren't clipped
    const centerDist = center.length();
    const clipRange = Math.max(maxSize, centerDist) * 30;

    let newPositionCamera = new Vector3(maxSize, maxSize, maxSize);
    camera.zoom = 1;
    camera.left = -(2 * maxSize);
    camera.bottom = -(2 * maxSize);
    camera.top = 2 * maxSize;
    camera.right = 2 * maxSize;
    camera.near = -clipRange;
    camera.far = clipRange;
    camera.position.set(
      Math.ceil(center.x + newPositionCamera.x),
      Math.ceil(center.y + newPositionCamera.y),
      Math.ceil(center.z + newPositionCamera.z)
    );
    camera.lookAt(center.x, center.y, center.z);
    camera.updateProjectionMatrix();
  }

  static createGridHelper(
    gridHelper: GridHelper,
    size: Vector3,
    scene: Scene,
    canvas: HTMLCanvasElement,
    center: Vector3,
    groundZ?: number // Optional ground Z for SoftJaws projects
  ) {
    this.disposeGridHelper(gridHelper, scene);

    const maxSize = Math.max(size.x, size.y, size.z);
    gridHelper = new GridHelper(maxSize * 2, Math.ceil(maxSize / 8), new Color('#000000'), new Color('#000000'));
    gridHelper.rotateX(Math.PI / 2);

    // Use provided groundZ if available (for SoftJaws), otherwise use bbox bottom
    const gridZ = groundZ !== undefined ? groundZ : center.z - size.z / 2;

    gridHelper.position.setZ(-size.z / 2 + 0.1); // Increased offset to make grid visible when camera is parallel to Z-axis

    const groundPlaneGeometry = new PlaneGeometry(maxSize * 2, maxSize * 2, 1, 1);
    const groundPlane = new Mesh(
      groundPlaneGeometry,
      new MeshPhysicalMaterial({ color: 0xffffff, side: FrontSide, transparent: true, opacity: 0.7 })
    );
    groundPlane.position.set(center.x, center.y, gridZ + 0.05);
    groundPlane.name = 'GridHelperGroundPlane';
    scene.add(groundPlane);

    const xLineMaterial = new LineMaterial({ color: new Color('#ff3653'), linewidth: 3 });
    xLineMaterial.resolution.set(
      canvas.clientWidth * window.devicePixelRatio,
      canvas.clientHeight * window.devicePixelRatio
    );

    const yLineMaterial = new LineMaterial({ color: new Color('#8adb00'), linewidth: 3 });
    yLineMaterial.resolution.set(
      canvas.clientWidth * window.devicePixelRatio,
      canvas.clientHeight * window.devicePixelRatio
    );

    const xLineGeometry = new LineGeometry();
    xLineGeometry.setPositions([
      center.x - maxSize,
      center.y - maxSize,
      gridZ + 0.1,
      center.x + maxSize,
      center.y - maxSize,
      gridZ + 0.1
    ]);
    const xLine = new Line2(xLineGeometry, xLineMaterial);
    xLine.computeLineDistances();
    xLine.scale.set(1, 1, 1);

    const yLineGeometry = new LineGeometry();
    yLineGeometry.setPositions([
      center.x - maxSize,
      center.y - maxSize,
      gridZ + 0.1,
      center.x - maxSize,
      center.y + maxSize,
      gridZ + 0.1
    ]);
    const yLine = new Line2(yLineGeometry, yLineMaterial);
    yLine.computeLineDistances();
    yLine.scale.set(1, 1, 1);

    scene.add(xLine);
    scene.add(yLine);
    scene.add(gridHelper);

    gridHelper.userData = { xLine, yLine, groundPlane };

    gridHelper.position.set(center.x, center.y, gridZ + 0.1);

    return gridHelper;
  }

  static disposeGridHelper(gridHelper: GridHelper, scene: Scene) {
    if (gridHelper) {
      // Remove and dispose the xLine
      const xLine = gridHelper.userData['xLine'];
      if (xLine) {
        scene.remove(xLine);
        xLine.geometry.dispose();
        xLine.material.dispose();
      }

      // Remove and dispose the yLine
      const yLine = gridHelper.userData['yLine'];
      if (yLine) {
        scene.remove(yLine);
        yLine.geometry.dispose();
        yLine.material.dispose();
      }

      // Remove and dispose the groundPlane
      const groundPlane = gridHelper.userData['groundPlane'];
      if (groundPlane) {
        scene.remove(groundPlane);
        groundPlane.geometry.dispose();
        groundPlane.material.dispose();
      }

      // Finally, remove and dispose the gridHelper itself
      scene.remove(gridHelper);
      gridHelper.geometry.dispose();
      gridHelper.material.dispose();

      // Clear userData to prevent memory leaks
      gridHelper.userData = {};
    }
  }

  static createOutlineEffectWithLineMaterial(session: SessionService) {
    return new OutlineWireEffect(session);
  }

  static createDimensionsHelper(engine: Engine, box: Box3): DimensionsHelper[] {
    this.disposeDimensionsHelper(engine, engine.dimensionsHelper);

    const unitProvider = (): { units: string; converter: (v: number) => number } => {
      const sys = engine.unitConversionService.unitSystem();
      return {
        units: engine.unitConversionService.getUnitLabel('length', sys),
        converter: (v: number) => engine.unitConversionService.convert(v, 'length', sys)
      };
    };

    const canvasContainer = document.getElementById('canvas-container');
    const ldx = new LinearDimension(canvasContainer, engine.renderer, engine.camera, '#000');
    const ldy = new LinearDimension(canvasContainer, engine.renderer, engine.camera, '#000');
    const ldz = new LinearDimension(canvasContainer, engine.renderer, engine.camera, '#000');
    ldx.unitProvider = unitProvider;
    ldy.unitProvider = unitProvider;
    ldz.unitProvider = unitProvider;
    const x = ldx.create(
      new Vector3(box.min.x, box.max.y, box.min.z),
      new Vector3(box.max.x, box.max.y, box.min.z),
      new Vector3(0, 0, 1)
    );
    const y = ldy.create(
      new Vector3(box.max.x, box.min.y, box.min.z),
      new Vector3(box.max.x, box.max.y, box.min.z),
      new Vector3(0, 0, 1)
    );
    const z = ldz.create(
      new Vector3(box.max.x, box.max.y, box.min.z),
      new Vector3(box.max.x, box.max.y, box.max.z),
      new Vector3(1, 0, 0)
    );
    engine.scene.add(x);
    engine.scene.add(y);
    engine.scene.add(z);

    return [ldx, ldy, ldz];
  }

  static disposeDimensionsHelper(engine: Engine, dimensionsHelpers: DimensionsHelper[]) {
    if (dimensionsHelpers) {
      dimensionsHelpers.forEach((dimensionHelper) => {
        // Remove the ArrowHelper (node) from the scene
        if (dimensionHelper.node) {
          engine.scene.remove(dimensionHelper.node);

          // Dispose of ArrowHelper's geometry and material
          if ((dimensionHelper.node as any).geometry) {
            (dimensionHelper.node as any).geometry.dispose();
          }
          if ((dimensionHelper.node as any).material) {
            (dimensionHelper.node as any).material.dispose();
          }

          // Optionally set node to null to ensure full cleanup
          (dimensionHelper.node as any) = null;
        }

        // Call dispose on the dimensionHelper itself, if it has such a method
        if (dimensionHelper.detach) {
          dimensionHelper.detach();
        }
      });
    }
  }
}

class OutlineWireEffectSelection {
  constructor(
    public _selection: Set<Object3D>,
    public session: SessionService
  ) { }

  add(v: Object3D) {
    this._selection.add(v);
    this.update(true);
  }

  clear() {
    this.update(false);
    this._selection.clear();
  }

  delete(v: Object3D) {
    const wasPresent = this._selection.delete(v);
    if (wasPresent) {
      // Reset the linewidth for the removed item
      const partId = v.userData?.['id'];
      if (partId) {
        const found = this.session.engine?.selector.objects.find((o: any) => partId === o.data.id);
        if (found) {
          found.wirematerial.linewidth = 1;
        }
      }
    }
    return wasPresent;
  }

  get() {
    return this._selection;
  }

  update(enable: boolean) {
    this.get().forEach((item) => {
      if (!item) return;
      const partId = item.userData['id'];
      const found = this.session.engine?.selector.objects.find((o: any) => partId === o.data.id);
      if (found) {
        found.wirematerial.linewidth = enable ? 3 : 1;
      }
    });
  }
}

export class OutlineWireEffect {
  public _selection: Set<Object3D> = new Set();
  public selection: OutlineWireEffectSelection;
  constructor(public session: SessionService) {
    this.selection = new OutlineWireEffectSelection(this._selection, this.session);
  }

  update() { }
}

class LinearDimension {
  cb: { onChange: never[] };
  config: {
    headLength: number;
    headWidth: number;
    units: string;
    unitsConverter: (v: number) => number;
    lineWidth: number;
    extensionLength: number;
    extensionGap: number;
  };
  unitProvider: (() => { units: string; converter: (v: number) => number }) | null = null;
  from: any;
  to: any;
  extrude: any;
  node!: Object3D<Object3DEventMap>;
  hidden: undefined;
  domElement: any;
  private lineMaterial: LineMaterial | null = null;
  private lineColor: Color;

  constructor(
    public domRoot: any,
    public renderer: any,
    public camera: any,
    public color: any
  ) {
    this.cb = {
      onChange: []
    };
    // Modern themed color - matches nav cube edge style
    this.lineColor = new Color('#6b7280'); // Neutral gray that works in light/dark
    this.config = {
      headLength: 0.8,
      headWidth: 0.5,
      units: 'mm',
      unitsConverter: function (v: number) {
        return v;
      },
      lineWidth: 2,
      extensionLength: 1.5,
      extensionGap: 0.3
    };
  }

  create(p0: any, p1: any, extrude: any) {
    this.from = p0;
    this.to = p1;
    this.extrude = extrude;

    this.node = new Object3D();
    this.hidden = undefined;

    // Create modern styled label element
    const el: any = document.createElement('div');
    el.id = `dim-${this.node.id}`;
    el.innerHTML = '';
    el.className = 'annotation-dimension-content';
    // Start hidden and off-screen to prevent flash on first toggle
    el.style.opacity = '0';
    el.style.display = 'none';
    el.style.transform = 'translate(-9999px, -9999px)';
    this.domRoot.appendChild(el);
    this.domElement = el;

    return this.node;
  }

  private createLine(points: number[]): Line2 {
    const geometry = new LineGeometry();
    geometry.setPositions(points);

    if (!this.lineMaterial) {
      this.lineMaterial = new LineMaterial({
        color: this.lineColor,
        linewidth: this.config.lineWidth,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
        depthWrite: false
      });
      this.lineMaterial.resolution.set(
        this.renderer.domElement.width,
        this.renderer.domElement.height
      );
    }

    const line = new Line2(geometry, this.lineMaterial);
    line.computeLineDistances();
    line.renderOrder = 9999; // Render on top of everything
    return line;
  }

  private createArrowHead(tip: Vector3, direction: Vector3): Line2 {
    const headLength = this.config.headLength;
    const headWidth = this.config.headWidth;

    // Create perpendicular vector for arrow wings
    const up = new Vector3(0, 0, 1);
    let perp = new Vector3().crossVectors(direction, up).normalize();
    if (perp.lengthSq() < 0.001) {
      perp = new Vector3().crossVectors(direction, new Vector3(0, 1, 0)).normalize();
    }

    const base = tip.clone().sub(direction.clone().multiplyScalar(headLength));
    const wing1 = base.clone().add(perp.clone().multiplyScalar(headWidth * 0.5));
    const wing2 = base.clone().sub(perp.clone().multiplyScalar(headWidth * 0.5));

    // Arrow head as two lines forming a V
    const points = [
      wing1.x, wing1.y, wing1.z,
      tip.x, tip.y, tip.z,
      wing2.x, wing2.y, wing2.z
    ];

    return this.createLine(points);
  }

  update(camera: any) {
    this.camera = camera;

    // Clear previous geometry
    while (this.node.children.length > 0) {
      const child = this.node.children[0];
      this.node.remove(child);
      if ((child as any).geometry) (child as any).geometry.dispose();
    }

    // Update line material resolution
    if (this.lineMaterial) {
      (this.lineMaterial as any).resolution.set(
        this.renderer.domElement.width,
        this.renderer.domElement.height
      );
    }

    const p0 = this.from;
    const p1 = this.to;
    const extrude = this.extrude;

    let pmin: Vector3, pmax: Vector3;

    if (extrude.x >= 0 && extrude.y >= 0 && extrude.z >= 0) {
      pmax = new Vector3(
        extrude.x + Math.max(p0.x, p1.x),
        extrude.y + Math.max(p0.y, p1.y),
        extrude.z + Math.max(p0.z, p1.z)
      );
      pmin = new Vector3(
        extrude.x < 1e-16 ? extrude.x + Math.min(p0.x, p1.x) : pmax.x,
        extrude.y < 1e-16 ? extrude.y + Math.min(p0.y, p1.y) : pmax.y,
        extrude.z < 1e-16 ? extrude.z + Math.min(p0.z, p1.z) : pmax.z
      );
    } else {
      pmax = new Vector3(
        extrude.x + Math.min(p0.x, p1.x),
        extrude.y + Math.min(p0.y, p1.y),
        extrude.z + Math.min(p0.z, p1.z)
      );
      pmin = new Vector3(
        extrude.x > -1e-16 ? extrude.x + Math.max(p0.x, p1.x) : pmax.x,
        extrude.y > -1e-16 ? extrude.y + Math.max(p0.y, p1.y) : pmax.y,
        extrude.z > -1e-16 ? extrude.z + Math.max(p0.z, p1.z) : pmax.z
      );
    }

    const direction = pmax.clone().sub(pmin).normalize();
    const distance = pmin.distanceTo(pmax);

    // Main dimension line (with gap in middle for label)
    const gapSize = Math.min(distance * 0.15, 8); // Gap for label
    const midPoint = pmin.clone().add(pmax).multiplyScalar(0.5);

    // Line from pmin to gap start
    const gapStart = midPoint.clone().sub(direction.clone().multiplyScalar(gapSize));
    const gapEnd = midPoint.clone().add(direction.clone().multiplyScalar(gapSize));

    // Create dimension line segments (with gap for label)
    const line1Points = [
      pmin.x, pmin.y, pmin.z,
      gapStart.x, gapStart.y, gapStart.z
    ];
    const line2Points = [
      gapEnd.x, gapEnd.y, gapEnd.z,
      pmax.x, pmax.y, pmax.z
    ];

    this.node.add(this.createLine(line1Points));
    this.node.add(this.createLine(line2Points));

    // Arrow heads at both ends pointing outward
    this.node.add(this.createArrowHead(pmin, direction.clone().negate()));
    this.node.add(this.createArrowHead(pmax, direction.clone()));

    // Extension lines (perpendicular ticks at the ends)
    const extLen = this.config.extensionLength;
    const extGap = this.config.extensionGap;

    // Find extension direction (perpendicular to dimension line, toward original points)
    const extDir = extrude.clone().normalize().negate();

    // Extension line at pmin
    const ext1Start = pmin.clone().add(extDir.clone().multiplyScalar(extGap));
    const ext1End = pmin.clone().sub(extDir.clone().multiplyScalar(extLen));
    this.node.add(this.createLine([
      ext1Start.x, ext1Start.y, ext1Start.z,
      ext1End.x, ext1End.y, ext1End.z
    ]));

    // Extension line at pmax
    const ext2Start = pmax.clone().add(extDir.clone().multiplyScalar(extGap));
    const ext2End = pmax.clone().sub(extDir.clone().multiplyScalar(extLen));
    this.node.add(this.createLine([
      ext2Start.x, ext2Start.y, ext2Start.z,
      ext2End.x, ext2End.y, ext2End.z
    ]));

    // Update label position
    if (this.domElement !== undefined) {
      const textPos = midPoint.clone().project(this.camera);

      // Check if behind camera
      if (textPos.z > 1) {
        this.domElement.style.opacity = '0';
        return;
      }

      this.domElement.style.opacity = '1';

      const clientX =
        (this.renderer.domElement.offsetWidth * (textPos.x + 1)) / 2 +
        this.renderer.domElement.offsetLeft;

      const clientY =
        (-this.renderer.domElement.offsetHeight * (textPos.y - 1)) / 2 +
        this.renderer.domElement.offsetTop;

      const unitConfig = this.unitProvider ? this.unitProvider() : { units: this.config.units, converter: this.config.unitsConverter };
      const value = unitConfig.converter(distance).toFixed(2);
      const dimLabel = unitConfig.units;
      this.domElement.textContent = '';
      const valueSpan = document.createElement('span');
      valueSpan.className = 'dim-value';
      valueSpan.textContent = value;
      const unitSpan = document.createElement('span');
      unitSpan.className = 'dim-unit';
      unitSpan.textContent = dimLabel;
      this.domElement.appendChild(valueSpan);
      this.domElement.appendChild(unitSpan);

      const dimWidth = this.domElement.offsetWidth;
      const dimHeight = this.domElement.offsetHeight;

      this.domElement.style.transform = `translate(${clientX - dimWidth / 2}px, ${clientY - dimHeight / 2}px)`;
    }
  }

  detach() {
    if (this.node && this.node.parent) {
      this.node.parent.remove(this.node);
    }
    // Dispose geometries
    this.node?.children?.forEach((child: any) => {
      if (child.geometry) child.geometry.dispose();
    });
    if (this.lineMaterial) {
      this.lineMaterial.dispose();
      this.lineMaterial = null;
    }
    if (this.domElement !== undefined) {
      this.domRoot.removeChild(this.domElement);
      this.domElement = undefined;
    }
  }

  toggle(visible: boolean) {
    if (this.node && this.node.parent) {
      this.node.visible = visible;
    }
    if (this.domElement !== undefined) {
      if (visible) {
        // Show element but keep opacity at 0 - update() will set correct position and opacity
        this.domElement.style.display = 'block';
      } else {
        this.domElement.style.display = 'none';
        this.domElement.style.opacity = '0';
      }
    }
  }
}
